import React from "react";
import { useNavigate } from "react-router-dom";

import {
  FaCar,
  FaMoneyBillWave,
  FaShoppingCart,
  FaHeartbeat,
  FaSignal,
  FaPaperPlane,
  FaHospital,
  FaIndustry,
  FaSeedling,
  FaBookOpen,
  FaTruck,
  FaBolt,
  FaPhotoVideo,
  FaHotel,
  FaCity,
  FaBalanceScale,
  FaUniversity,
  FaShieldAlt,
  FaHardHat,
  FaBookReader,
  FaBus,
  FaBook,
  FaLandmark,
  FaBomb,
  FaFilm,
} from "react-icons/fa";

import heroImage from "../../assets/ai-illustration.png";

const styles = `
  /* ── Reset ── */
  * { box-sizing: border-box; }

  /* ── Page wrapper ── */
  .ai-page {
    background: #22c7b8;
    min-height: 100vh;
  }

  /* ── Hero ── */
  .ai-hero {
    width: 100%;
    background: #173b8f;
  }

  .ai-hero-inner {
    width: 95%;
    max-width: 1450px;
    margin: 0 auto;
    padding: 55px 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 40px;
    flex-wrap: wrap;
  }

  .ai-hero-left {
    flex: 1;
    min-width: 280px;
  }

  .ai-hero-h1 {
    color: #ffffff;
    font-size: 42px;
    font-weight: 700;
    margin-bottom: 18px;
    font-family: "Exo", sans-serif;
    line-height: 1.15;
  }

  .ai-hero-p {
    color: #f2f2f2;
    font-size: 16px;
    line-height: 1.9;
    max-width: 680px;
    margin: 0;
  }

  .ai-hero-right {
    flex: 1;
    min-width: 260px;
    text-align: center;
  }

  .ai-hero-img {
    width: 100%;
    max-width: 430px;
    object-fit: contain;
    height: auto;
  }

  /* ── Industries Grid ── */
  .ai-grid-section {
    width: 95%;
    max-width: 1500px;
    margin: 0 auto;
    padding: 45px 0 70px;
  }

  .ai-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 22px;
  }

  /* ── Card ── */
  .ai-card {
    background: #ffffff;
    border-radius: 14px;
    padding: 24px 18px;
    text-align: center;
    min-height: 220px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 6px 16px rgba(0,0,0,0.08);
  }

  .ai-card-icon {
    font-size: 38px;
    color: #173b8f;
    margin-bottom: 16px;
  }

  .ai-card-title {
    color: #173b8f;
    font-size: 18px;
    line-height: 1.45;
    font-weight: 700;
    margin-bottom: 18px;
    font-family: "Exo", sans-serif;
  }

  .ai-card-btn {
    background: #173b8f;
    color: #ffffff;
    border: none;
    padding: 10px 20px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0,0,0,0.12);
    white-space: nowrap;
  }

  .ai-card-btn:hover {
    background: #1a4ab5;
  }

  /* ── Ultra-wide: 1920px+ ── */
  @media (min-width: 1920px) {
    .ai-hero-inner {
      padding: 70px 0;
    }
    .ai-hero-h1 {
      font-size: 52px;
    }
    .ai-hero-p {
      font-size: 18px;
    }
    .ai-grid {
      grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
      gap: 28px;
    }
    .ai-card {
      padding: 30px 22px;
      min-height: 240px;
    }
    .ai-card-icon {
      font-size: 44px;
    }
    .ai-card-title {
      font-size: 20px;
    }
    .ai-grid-section {
      padding: 55px 0 90px;
    }
  }

  /* ── Large Desktop: 1400px – 1919px ── */
  @media (max-width: 1919px) and (min-width: 1400px) {
    .ai-hero-h1 {
      font-size: 46px;
    }
    .ai-grid {
      gap: 24px;
    }
  }

  /* ── Small Desktop / Laptop: 1024px – 1399px ── */
  @media (max-width: 1399px) and (min-width: 1024px) {
    .ai-hero-h1 {
      font-size: 38px;
    }
    .ai-grid {
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 18px;
    }
    .ai-card {
      padding: 20px 16px;
      min-height: 210px;
    }
    .ai-card-icon {
      font-size: 34px;
    }
    .ai-card-title {
      font-size: 16px;
    }
  }

  /* ── Tablet Portrait: 768px – 1023px ── */
  @media (max-width: 1023px) and (min-width: 768px) {
    .ai-hero-inner {
      padding: 40px 0;
      gap: 28px;
    }
    .ai-hero-h1 {
      font-size: 32px;
      margin-bottom: 14px;
    }
    .ai-hero-p {
      font-size: 15px;
    }
    .ai-hero-left {
      min-width: 240px;
    }
    .ai-hero-right {
      min-width: 200px;
    }
    .ai-hero-img {
      max-width: 320px;
    }
    .ai-grid {
      grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
      gap: 16px;
    }
    .ai-card {
      padding: 20px 14px;
      min-height: 200px;
    }
    .ai-card-icon {
      font-size: 32px;
      margin-bottom: 12px;
    }
    .ai-card-title {
      font-size: 15px;
      margin-bottom: 14px;
    }
    .ai-card-btn {
      padding: 9px 16px;
      font-size: 12px;
    }
    .ai-grid-section {
      padding: 35px 0 55px;
    }
  }

  /* ── Large Mobile: 481px – 767px ── */
  @media (max-width: 767px) and (min-width: 481px) {
    .ai-hero-inner {
      padding: 32px 0;
      gap: 24px;
      flex-direction: column;
    }
    .ai-hero-left {
      min-width: unset;
      width: 100%;
    }
    .ai-hero-right {
      min-width: unset;
      width: 100%;
    }
    .ai-hero-h1 {
      font-size: 28px;
      margin-bottom: 12px;
    }
    .ai-hero-p {
      font-size: 14px;
      line-height: 1.8;
    }
    .ai-hero-img {
      max-width: 280px;
    }
    .ai-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 14px;
    }
    .ai-card {
      padding: 18px 12px;
      min-height: 190px;
    }
    .ai-card-icon {
      font-size: 30px;
      margin-bottom: 12px;
    }
    .ai-card-title {
      font-size: 14px;
      margin-bottom: 14px;
    }
    .ai-card-btn {
      padding: 9px 14px;
      font-size: 12px;
    }
    .ai-grid-section {
      padding: 28px 0 50px;
    }
  }

  /* ── Small Mobile: up to 480px ── */
  @media (max-width: 480px) {
    .ai-hero-inner {
      padding: 26px 0;
      gap: 20px;
      flex-direction: column;
    }
    .ai-hero-left {
      min-width: unset;
      width: 100%;
    }
    .ai-hero-right {
      min-width: unset;
      width: 100%;
    }
    .ai-hero-h1 {
      font-size: 24px;
      margin-bottom: 10px;
    }
    .ai-hero-p {
      font-size: 14px;
      line-height: 1.75;
    }
    .ai-hero-img {
      max-width: 240px;
    }
    .ai-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
    }
    .ai-card {
      padding: 16px 10px;
      min-height: 175px;
      border-radius: 10px;
    }
    .ai-card-icon {
      font-size: 28px;
      margin-bottom: 10px;
    }
    .ai-card-title {
      font-size: 13px;
      margin-bottom: 12px;
      line-height: 1.4;
    }
    .ai-card-btn {
      padding: 8px 12px;
      font-size: 11px;
      border-radius: 6px;
    }
    .ai-grid-section {
      padding: 22px 0 40px;
    }
  }

  /* ── Extra Small: up to 400px ── */
  @media (max-width: 400px) {
    .ai-hero-h1 {
      font-size: 21px;
    }
    .ai-hero-p {
      font-size: 13px;
    }
    .ai-hero-img {
      max-width: 200px;
    }
    .ai-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 10px;
    }
    .ai-card {
      padding: 14px 8px;
      min-height: 160px;
    }
    .ai-card-icon {
      font-size: 26px;
      margin-bottom: 8px;
    }
    .ai-card-title {
      font-size: 12px;
      margin-bottom: 10px;
    }
    .ai-card-btn {
      padding: 7px 10px;
      font-size: 11px;
    }
  }

  /* ── Very Small: up to 360px ── */
  @media (max-width: 360px) {
    .ai-hero-h1 {
      font-size: 19px;
    }
    .ai-hero-p {
      font-size: 12.5px;
    }
    .ai-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 8px;
    }
    .ai-card {
      padding: 12px 7px;
      min-height: 150px;
    }
    .ai-card-icon {
      font-size: 24px;
    }
    .ai-card-title {
      font-size: 11.5px;
    }
    .ai-card-btn {
      padding: 6px 9px;
      font-size: 10.5px;
    }
  }
`;

const AIAgentsPage = () => {
  const navigate = useNavigate();

  const industries = [
    {
      title: "Automotive Industry",
      icon: <FaCar />,
      path: "/automotive-industry",
    },
    {
      title: "Financial Sector",
      icon: <FaMoneyBillWave />,
      path: "/financial-sector",
    },
    {
      title: "The Retail Industry",
      icon: <FaShoppingCart />,
      path: "/retail-industry",
    },
    {
      title: "Life sciences sector",
      icon: <FaHeartbeat />,
      path: "/life-sciences",
    },
    {
      title: "Telecom sector",
      icon: <FaSignal />,
      path: "/telecom-sector",
    },
    {
      title: "Aerospace & Aviation",
      icon: <FaPaperPlane />,
      path: "/aerospace-aviation",
    },
    {
      title: "Healthcare & Hospitals",
      icon: <FaHospital />,
      path: "/healthcare-and-hospitals",
    },
    {
      title: "Manufacturing & Industrial",
      icon: <FaIndustry />,
      path: "/manufacturing-industrial",
    },
    {
      title: "Agriculture & Agritech",
      icon: <FaSeedling />,
      path: "/agriculture-agritech",
    },
    {
      title: "Education & EdTech",
      icon: <FaBookReader />,
      path: "/education-edtech",
    },
    {
      title: "Logistics & Supply Chain",
      icon: <FaTruck />,
      path: "/logistics-supply-chain",
    },
    {
      title: "Energy & Utilities",
      icon: <FaBolt />,
      path: "/energy-utilities",
    },
    {
      title: "Media & Entertainment",
      icon: <FaFilm />,
      path: "/media-entertainment",
    },
    {
      title: "Tourism & Hospitality",
      icon: <FaHotel />,
      path: "/tourism-hospitality",
    },
    {
      title: "Legal & Compliance",
      icon: <FaBook />,
      path: "/legal-compliance",
    },
    {
      title: "Public Sector / Government",
      icon: <FaLandmark />,
      path: "/public-sector-government",
    },
    {
      title: "Defense & Security",
      icon: <FaBomb />,
      path: "/defense-security",
    },
    {
      title: "Mining & Resources",
      icon: <FaHardHat />,
      path: "/mining-natural-resources",
    },
  ];

  return (
    <>
      <style>{styles}</style>

      <div className="ai-page">
        {/* HERO SECTION */}
        <div className="ai-hero">
          <div className="ai-hero-inner">
            {/* LEFT CONTENT */}
            <div className="ai-hero-left">
              <h1 className="ai-hero-h1">AI Agents</h1>
              <p className="ai-hero-p">
                AI agents are intelligent systems capable of perceiving their
                environment, making decisions, and taking autonomous actions to
                achieve specific goals. Unlike traditional software, they can
                learn and adapt, making them powerful tools for complex
                problem-solving across various industries.
              </p>
            </div>

            {/* RIGHT IMAGE */}
            <div className="ai-hero-right">
              <img src={heroImage} alt="AI" className="ai-hero-img" />
            </div>
          </div>
        </div>

        {/* INDUSTRIES GRID */}
        <div className="ai-grid-section">
          <div className="ai-grid">
            {industries.map((item, index) => (
              <div key={index} className="ai-card">
                <div>
                  <div className="ai-card-icon">{item.icon}</div>
                  <h2 className="ai-card-title">{item.title}</h2>
                </div>
                <button
                  className="ai-card-btn"
                  onClick={() => navigate(item.path)}
                >
                  Learn More
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default AIAgentsPage;
